import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapView } from "../components/MapView";
import {
  MaBottomNav, MaCircleButton, MaIcon, MaSectionHeader, MaStat,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import {
  useAuthStatus, useFeed, useFriends, useImportedEvents, useLists, useLogout, useSaves,
} from "../lib/queries";
import { TOTAL } from "../data/beenThere";
import { getChecked } from "../lib/beenThereStore";

/** Compact checklist entry card. Used on Profile + Saved so the
 *  feature is reachable from both tabs without duplicating the data
 *  pull (it's just a localStorage read). */
function BeenThereCard({ onClick }: { onClick: () => void }) {
  const [doneCount, setDoneCount] = useState<number>(() => getChecked().size);
  useEffect(() => {
    const onFocus = () => setDoneCount(getChecked().size);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
  const pct = Math.round((doneCount / TOTAL) * 100);
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 14, width: "100%",
        padding: "14px 16px",
        background: "var(--ma-cream)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "var(--ma-card-radius)",
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{
        width: 48, height: 48, flexShrink: 0,
        background: "var(--ma-brick)",
        border: "var(--ma-border) solid var(--ma-ink)",
        borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 24,
      }} aria-hidden>📌</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ma-mono" style={{
          fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase",
          color: "var(--ma-brick)", marginBottom: 2,
        }}>
          your hit list
        </div>
        <div className="ma-display" style={{ fontSize: 18, color: "var(--ma-ink)", lineHeight: 1 }}>
          Been there, done that
        </div>
        <div style={{
          marginTop: 8, height: 6,
          background: "var(--ma-paper)",
          border: "1px solid var(--ma-ink)",
          borderRadius: 3, overflow: "hidden",
        }}>
          <div style={{
            width: `${pct}%`, height: "100%",
            background: pct === 100 ? "var(--ma-canal)" : "var(--ma-brick)",
          }} />
        </div>
        <div className="ma-mono" style={{
          fontSize: 9, letterSpacing: "0.08em", color: "var(--ma-ink-soft)", marginTop: 4,
        }}>
          {doneCount} of {TOTAL} done
        </div>
      </div>
      <MaIcon name="arrow" size={16} stroke={2} color="var(--ma-ink)" />
    </button>
  );
}

export function Profile() {
  const navigate = useNavigate();
  // Use useAuthStatus, not useMe directly: useMe is `enabled: false` when
  // no token is stored, and a disabled query reports `isPending: true`
  // forever — which would trap anon users on the loading screen instead
  // of bouncing them into the sign-in CTA below.
  const { status: authStatus, user: me } = useAuthStatus();
  const { data: saves = [] } = useSaves();
  const { data: lists = [] } = useLists();
  const { data: friends = [] } = useFriends();
  const { data: feed = [] } = useFeed();
  const { data: events = [] } = useImportedEvents({ located: true, upcoming: true, limit: 200 });

  // Real numbers from real endpoints.
  const visited = feed.filter((it: any) => it.user_id === me?.id).length;
  const savedCount = saves.length;
  const listCount = lists.length;
  const friendCount = friends.length;

  // Year-so-far rolled from feed (own check-ins this year).
  const yearStart = Math.floor(new Date(new Date().getUTCFullYear(), 0, 1).getTime() / 1000);
  const thisYear = feed.filter((it: any) => it.user_id === me?.id && it.created_at >= yearStart);
  const neighborhoodsVisited = new Set(thisYear.map((it: any) => it.venue_id)).size;

  // Real pins for the mini map: the events I might have checked in to, plus
  // what's nearby today. For v1 we just show the imported events I could go to.
  const miniPins = events
    .filter((e) => e.lat != null && e.lng != null)
    .slice(0, 80)
    .map((e) => ({ id: e.id, title: e.title, lat: e.lat!, lng: e.lng!, cat: e.category ?? null, live: false }));

  const memberSince = me ? new Date(((me as any).createdAt ?? Date.now() / 1000) * 1000).getUTCFullYear() : null;

  if (authStatus === "loading") {
    return <div className="ma-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}><span className="ma-mono" style={{ fontSize: 12, color: "var(--ma-ink-soft)" }}>loading…</span></div>;
  }

  if (!me) {
    return (
      <div className="ma-screen" style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ flex: 1, padding: "calc(env(safe-area-inset-top, 20px) + 34px) 24px 0", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--ma-brick)", marginBottom: 10 }}>
            optional · you can keep browsing
          </div>
          <div className="ma-display" style={{ fontSize: 38, lineHeight: 0.95, color: "var(--ma-ink)", marginBottom: 14 }}>
            Sign in to save<br />and follow.
          </div>
          <div style={{ fontFamily: "var(--ma-body)", fontSize: 15, lineHeight: 1.45, color: "var(--ma-ink-soft)", marginBottom: 24 }}>
            One-click email magic link. No password. We'll remember you for a year. The map and events work fine without signing in.
          </div>
          <button onClick={() => navigate("/login")} style={{
            ...btnDark, flex: "none", width: "100%", padding: "16px 0",
            fontFamily: "var(--ma-display)", fontSize: 16, fontWeight: 700,
          }}>
            Send me a magic link
          </button>

          {/* Checklist is a localStorage feature — anon users get the
              same experience as authed. Show the card here so it's
              reachable from /you even without an account. */}
          <div style={{ marginTop: 22 }}>
            <BeenThereCard onClick={() => navigate("/been-there")} />
          </div>
        </div>
        <MaBottomNav active="profile" />
      </div>
    );
  }

  return (
    <Screen footer={<MaBottomNav active="profile" />}>
      <div style={{
        padding: "calc(env(safe-area-inset-top, 20px) + 34px) 16px 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "var(--ma-ink-soft)",
        }}>
          {memberSince ? `Member · since ${memberSince}` : "Member"}
        </div>
        <MaCircleButton icon="filter" />
      </div>

      <div style={{ padding: "18px 16px 8px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 14 }}>
          <div style={{
            width: 84, height: 84,
            background: `var(--ma-${me.avatarTone || "brick"})`,
            color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--ma-display)", fontSize: 44, fontWeight: 800,
          }}>{me.avatarInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ma-display" style={{ fontSize: 32, color: "var(--ma-ink)", lineHeight: 0.95 }}>
              {me.name}
            </div>
            <div className="ma-mono" style={{
              fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 6,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              @{me.handle}{me.neighborhood ? ` · ${me.neighborhood}` : ""}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "8px 16px 18px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        <MaStat value={visited} label="Visited" onClick={() => {
          // Scroll to the check-ins section below.
          document.getElementById("profile-checkins")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }} />
        <MaStat value={savedCount} label="Saved" onClick={() => navigate("/saved")} />
        <MaStat value={listCount} label="Lists" onClick={() => navigate("/saved")} />
        <MaStat value={friendCount} label="Friends" onClick={() => navigate("/you/friends")} />
      </div>

      {/* Interests */}
      {me.interests && me.interests.length > 0 && (
        <div style={{ padding: "0 16px 18px" }}>
          <MaSectionHeader label="Your interests" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {me.interests.map((id) => (
              <span key={id} className="ma-chip" style={{ textTransform: "uppercase" }}>{id}</span>
            ))}
          </div>
        </div>
      )}

      {/* Real map */}
      <div style={{ padding: "0 16px 18px" }}>
        <MaSectionHeader label="Nearby tonight" action={`${miniPins.length} spots`} />
        <div style={{
          position: "relative",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: "var(--ma-card-radius)",
          overflow: "hidden", height: 240,
        }}>
          <MapView pins={miniPins} fitToPins={miniPins.length > 0 && miniPins.length < 60} interactive={false} />
        </div>
      </div>

      {/* Real activity */}
      <div id="profile-checkins" style={{ padding: "0 16px 18px" }}>
        <MaSectionHeader label={`Check-ins · ${thisYear.length} this year`} />
        {thisYear.length === 0 ? (
          <div style={{
            padding: "14px 16px", background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
          }}>
            <div style={{ fontFamily: "var(--ma-body)", fontSize: 14, color: "var(--ma-ink)", lineHeight: 1.4 }}>
              No check-ins yet. Visit a venue and tap <strong>Check in</strong> to start a log.
            </div>
            <button onClick={() => navigate("/discover")} style={{
              marginTop: 10, padding: "8px 14px",
              background: "var(--ma-brick)", color: "var(--ma-paper)",
              border: "var(--ma-border) solid var(--ma-ink)",
              borderRadius: "var(--ma-card-radius)",
              fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
              textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            }}>
              Open the map
            </button>
          </div>
        ) : (
          <div style={{
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            overflow: "hidden",
          }}>
            {thisYear.slice(0, 10).map((it: any, i, arr) => (
              <button key={it.id} onClick={() => navigate(`/venue/${it.venue_id}`)} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", width: "100%",
                background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
                borderBottom: i === arr.length - 1 ? "none" : "1px dashed var(--ma-line)",
              }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--ma-brick)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--ma-body)", fontWeight: 600, fontSize: 14, color: "var(--ma-ink)" }}>
                    {it.venue_name}
                  </div>
                  {it.note && (
                    <div style={{ fontFamily: "var(--ma-body)", fontSize: 12, color: "var(--ma-ink-soft)",
                      marginTop: 2, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 1, overflow: "hidden" }}>
                      "{it.note}"
                    </div>
                  )}
                </div>
                <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)", whiteSpace: "nowrap" }}>
                  {new Date(it.created_at * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Been there done that — public to authed users too. */}
      <div style={{ padding: "0 16px 14px" }}>
        <BeenThereCard onClick={() => navigate("/been-there")} />
      </div>

      {me.role === "admin" && (
        <div style={{ padding: "0 16px 14px" }}>
          <button onClick={() => navigate("/admin")} style={{
            width: "100%", padding: "14px",
            background: "var(--ma-brick)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.14em",
            textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            Open admin console →
          </button>
        </div>
      )}

      <div style={{ padding: "0 16px 18px", display: "flex", gap: 10 }}>
        <button onClick={() => navigate("/you/friends")} style={btnLight}>Friends</button>
        <LogoutButton />
      </div>

    </Screen>
  );
}

function LogoutButton() {
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <button
      onClick={async () => { await logout.mutateAsync(); navigate("/welcome"); }}
      style={btnDark}
    >
      Log out
    </button>
  );
}

const btnLight: React.CSSProperties = {
  flex: 1, padding: "12px",
  background: "var(--ma-cream)", color: "var(--ma-ink)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
const btnDark: React.CSSProperties = {
  flex: 1, padding: "12px",
  background: "var(--ma-ink)", color: "var(--ma-paper)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
  textTransform: "uppercase", fontWeight: 600, cursor: "pointer",
};
